import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  Car,
  ChatCircle,
  CircleNotch as Loader2,
  MapPin,
} from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import CarCard from '../components/CarCard';
import TestDriveModal from '../components/TestDriveModal';
import { useCars } from '../context/CarsContext';
import { HeroSearch } from './home/HeroSearch';
import { useContactInfo } from '../hooks/useContactInfo';
import { useDragScroll } from '../hooks/useDragScroll';
import { useMediaQuery } from '../hooks/useMediaQuery';
import { useHidePillOver } from '../hooks/useHidePillOver';
import { SmartFormDemo } from './shared/SmartFormDemo';
import { PlacesBlock } from './shared/PlacesBlock';
import Reveal from './shared/Reveal';
import { CurlyArrow } from './shared/CurlyArrow';
import { useTranslation } from '../i18n/I18nContext';
import { config } from '../config/runtimeConfig';
import './page-layout.css';
import './HomePage.css';

/** Сколько машин в ленте главной: одна строка, дальше — весь каталог. */
const FEATURED_LIMIT = 10;

const HomePage = () => {
  const { t } = useTranslation();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const [selectedCar, setSelectedCar] = useState(null);

  const { getHotOffers, getAllCars, loading, error } = useCars();

  // Раньше здесь звали `openVoiceAssistantWithProvider('livekit')`, но эту
  // функцию снёс `3267f66c4` («remove ElevenLabs support», 23.12.2025), а
  // вызов остался — главная CTA витрины с тех пор бросала
  // `TypeError: openVoiceAssistantWithProvider is not a function` и не делала
  // ничего (#1330).
  const contactInfo = useContactInfo();

  const showroomCount = contactInfo.locations?.length ?? 0;

  const hotOffers = getHotOffers();
  const allCars = getAllCars();
  /* Секция месяцами стояла пустой: `getHotOffers()` фильтрует по `isHotOffer`,
     а инвентарь этого поля не присылает вовсе — на витрине зияла надпись «No
     hot offers available». Спецпредложения показываем, когда они есть; иначе
     первые машины стока, чтобы главная всегда показывала товар (#1330). */
  /* Не больше десяти — одна лента. Обрезка стоит и на спецпредложениях: их в
     стоке может оказаться сколько угодно, а лента в полсотни карточек — уже
     не витрина, а каталог без фильтров. Остальное — по кнопке в шапке. */
  const featuredCars = (hotOffers.length > 0 ? hotOffers : allCars).slice(
    0,
    FEATURED_LIMIT
  );

  const rail = useDragScroll();
  // На телефоне вход в каталог — кнопка-стрелка без подписи (#1456): рядом с
  // заголовком подпись не помещалась, и кнопка уезжала под него.
  const isWide = useMediaQuery('(min-width: 768px)');
  const viewAllLabel = t('showcase.home.viewAll', 'View all cars');
  // Пилюля ассистента не ложится на форму поиска: пока карточка в нижней
  // полосе экрана, пилюля уезжает (на телефоне она закрывала кнопку).
  const heroDockRef = useRef(null);
  useHidePillOver(heroDockRef);

  const handleBookTestDrive = (car) => {
    setSelectedCar(car);
    setIsModalOpen(true);
  };

  return (
    <div className="page">
      {/* Hero Section */}
      <section className="home-hero home-hero--framed">
        <div className="home-hero__inner">
          <div className="home-section">
            {/* Снимок дороги во всю карточку, а в его нижний левый угол врезан
                блок с заголовком и поиском. Врезка — не наложение: у неё фон
                самой страницы, и снимок обходит её обратным скруглением, то
                есть выглядит вырезанным, а не перекрытым. */}
            <div className="home-hero__photo">
              {/* Машина БЕЗ ФОНА, силуэтом на сером полотне героя: фон ей
                  даёт сам кадр, и снимок не приносит с собой чужую сцену.
                  `srcSet` нет: исходник один. */}
              <img
                className="home-hero__photo-img"
                src="/assets/hero-car.webp"
                alt=""
                width="1400"
                height="730"
              />

              <div className="home-hero__panel">
                <h1 className="home-hero__title">
                  {t('showcase.home.title', 'Find your dream car')}
                </h1>
                <p className="home-hero__lead">
                  {t(
                    'showcase.home.lead',
                    'Search the whole stock from both showrooms, or ask the assistant — it\u00a0answers straight away, day or night.'
                  )}
                </p>
                {/* Три факта под подзаголовком (#1335, вариант «B · Facts»):
                    закрывают пустую середину кадра. Только то, что уже есть
                    на странице: число машин — то же, что на кнопке поиска,
                    салоны — из контактов, ассистент — из подзаголовка. Пока
                    сток не загрузился, плашки с числом нет: «0 cars» читался
                    бы пустым складом. Только витрина: у дилера по токену
                    герой без них. */}
                {config.IS_OWN_STOCK && (
                  <ul className="home-hero__facts">
                    {allCars.length > 0 && (
                      <li className="home-hero__fact">
                        <Car size={20} aria-hidden="true" />
                        {t(
                          allCars.length === 1
                            ? 'showcase.home.factStockOne'
                            : 'showcase.home.factStockMany',
                          allCars.length === 1
                            ? '{count} car in stock'
                            : '{count} cars in stock'
                        ).replace('{count}', allCars.length)}
                      </li>
                    )}
                    {showroomCount > 0 && (
                      <li className="home-hero__fact">
                        <MapPin size={20} aria-hidden="true" />
                        {t(
                          showroomCount === 1
                            ? 'showcase.home.factShowroomsOne'
                            : 'showcase.home.factShowroomsMany',
                          showroomCount === 1
                            ? '{count} showroom'
                            : '{count} showrooms'
                        ).replace('{count}', showroomCount)}
                      </li>
                    )}
                    <li className="home-hero__fact">
                      <ChatCircle size={20} aria-hidden="true" />
                      {t('showcase.home.factAssistant', 'Assistant 24/7')}
                    </li>
                  </ul>
                )}
              </div>
            </div>

            {/* Поиск по стоку — белая карточка НА нижней кромке кадра, по
                центру (#1530): верхняя половина на снимке, нижняя под ним.
                Вне кадра, а не внутри: у кадра `overflow: clip`, и подсказки,
                открытые вниз, он срезал бы по своей кромке. До этого форма
                была третьей полкой левой врезки (#1330). */}
            <div className="home-hero__dock" ref={heroDockRef}>
              <HeroSearch panel={false} cars={allCars} />
            </div>
          </div>
        </div>
      </section>

      {/* Слой ниже героя. Он скруглён сверху и едет ПОВЕРХ героя: герой
          прилипает к верху экрана и остаётся под ним. Обёртка нужна ровно
          для этого — фон и слой должны быть общими для всей страницы ниже,
          иначе герой просвечивал бы между секциями. */}
      <div className="home-below">
        {/* Витрина: заголовок и кнопка «все машины» в одной строке, карточки —
          горизонтальной лентой. Ленту выбрали вместо сетки намеренно: у
          дилера в стоке десятки машин, и сетка на главной либо обрезает их
          произвольным числом, либо растёт на три экрана. Лента показывает,
          что за краем есть ещё. */}
        <Reveal as="section" className="home-section home-section--sm">
          <div className="page__container page__container--narrow">
            {/* Заголовок слева, вход во весь каталог справа — в одной строке:
                под лентой кнопка терялась бы за её правым краем, а в шапке она
                видна сразу, вместе с тем, что она продолжает. */}
            <div className="home-section__head home-section__head--row">
              <div className="home-section__head-text">
                <h2 className="home-section__title">
                  {hotOffers.length > 0
                    ? t('showcase.home.offers', 'Special offers')
                    : t('showcase.home.arrivals', 'Latest arrivals')}
                </h2>
                <p className="home-section__lead">
                  {hotOffers.length > 0
                    ? t(
                        'showcase.home.offersLead',
                        "Don't miss these deals on vehicles from our stock"
                      )
                    : t(
                        'showcase.home.arrivalsLead',
                        'The newest vehicles in our stock'
                      )}
                </p>
              </div>
              {featuredCars.length > 0 &&
                /* Вторичной кнопкой: главное в секции — сами машины, а в
                   шапке кнопка стоит рядом с заголовком, и залитая синяя
                   перетягивала на себя первый взгляд. Кнопка-ссылка, а не
                   `<Link>` вокруг `<button>`: вложенные интерактивные
                   элементы читалка видела двумя (#1335). */
                (isWide ? (
                  <Button
                    as={Link}
                    to="/cars"
                    variant="secondary"
                    arrow
                    className="home-section__action"
                  >
                    {viewAllLabel}
                  </Button>
                ) : (
                  <Button
                    as={Link}
                    to="/cars"
                    variant="secondary"
                    size="icon"
                    className="home-section__action"
                    aria-label={viewAllLabel}
                  >
                    <ArrowRight size={20} aria-hidden="true" />
                  </Button>
                ))}
            </div>

            {loading ? (
              <div className="home-state">
                <Loader2 size={32} className="home-state__spinner" />
                <span className="home-state__text">
                  {t('showcase.catalog.loading', 'Loading cars...')}
                </span>
              </div>
            ) : error ? (
              <div className="home-state">
                {/* Технический текст ошибки (`HTTP error! status: 500`)
                    остаётся в консоли — его логирует CarsContext. Покупателю
                    показываем действие, а не диагностику. */}
                <p className="home-state__error">
                  {t(
                    'showcase.catalog.loadError',
                    'Couldn\u2019t load the catalog. Please try again.'
                  )}
                </p>
                <Button onClick={() => window.location.reload()}>
                  {t('showcase.catalog.retry', 'Retry')}
                </Button>
              </div>
            ) : featuredCars.length > 0 ? (
              <ul className="home-rail" ref={rail.ref} {...rail.handlers}>
                {featuredCars.map((car) => (
                  <li key={car.id} className="home-rail__item">
                    {/* Свайп по фото — ленте, а не галерее карточки. */}
                    <CarCard
                      car={car}
                      onBookTestDrive={handleBookTestDrive}
                      swipePhotos={false}
                    />
                  </li>
                ))}
              </ul>
            ) : (
              <div className="home-state">
                <p className="page__muted">
                  {t(
                    'showcase.home.stockUpdating',
                    'The showroom stock is being updated — check back shortly'
                  )}
                </p>
              </div>
            )}
          </div>
        </Reveal>

        {contactInfo.locations?.length > 0 && (
          <Reveal as="section" className="home-section home-places-band">
            <div className="page__container page__container--narrow">
              {/* Салоны — общий блок с «Showroom» (#1335, вариант «A»):
                  слева заголовок, подпись и адреса (без часов — как на
                  макете), справа общая карта. Блок — на белой полосе во
                  всю ширину со скруглением. */}
              <PlacesBlock
                locations={contactInfo.locations}
                details={false}
                title={t('showcase.home.placesTitle', 'Visit our showrooms')}
                lead={t(
                  'showcase.home.placesLead',
                  'Come in for a test drive, or ask the assistant anything before you do'
                )}
              />
            </div>
          </Reveal>
        )}
        {/* CTA. Серое теперь у самой плашки, а не у полосы во всю ширину:
            страница собрана карточками, и полоса читалась среди них чужой. */}
        <Reveal as="section" className="home-section">
          <div className="page__container page__container--narrow">
            <div className="home-cta">
              <div className="home-cta__card">
                <div className="home-cta__body">
                  {/* Только текст: плашка, наклон и ключ-стикер сняты. Приём
                      перетягивал внимание на себя — рядом форма, и это она
                      здесь главное действие. */}
                  <div className="home-cta__text">
                    <h2 className="home-section__title">
                      {t(
                        'showcase.home.ctaTitle',
                        'Ready to find your perfect car?'
                      )}
                    </h2>
                    <p className="home-section__lead">
                      {t(
                        'showcase.home.ctaLead',
                        'Visit our showroom or contact us today'
                      )}
                    </p>

                    {/* Стрелка от обращения к кнопке формы: связка «вот
                        предложение — вот куда ответить». Петля, а не дуга:
                        промежуток между колонками широкий и низкий, и прямая
                        дуга на нём читалась разделителем. */}
                    <CurlyArrow
                      shape="loop"
                      startDot
                      className="home-cta__arrow"
                    />
                  </div>
                  {/* Под текстом — форма, а не кнопка (#1330). Кнопка уводила на
                  «Контакты» и просила сделать ещё один шаг; форма ловит лид
                  прямо здесь и продолжает разговор ассистентом. Список «что
                  дальше» выключен: над формой уже стоят заголовок и подпись. */}
                  <div className="home-cta__actions">
                    <SmartFormDemo showSteps={false} hideTitle />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Reveal>
      </div>

      <TestDriveModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        selectedCar={selectedCar}
        cars={allCars}
      />
    </div>
  );
};

export default HomePage;
